import { unzip as fflateUnzip } from "fflate";
import JSZip from "jszip";

// Every caller passes the `ArrayBuffer` body of a `responseType: "arraybuffer"` axios response;
// both branches below (`new Uint8Array` and JSZip `loadAsync`) accept that directly.
export async function unzip(zippedData: ArrayBuffer, isJson = true, signal?: AbortSignal) {
  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }
  if (isJson) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      fflateUnzip(new Uint8Array(zippedData), async (err, files) => {
        if (err) {
          return reject(err);
        }
        if (signal?.aborted) {
          return reject(new DOMException("Aborted", "AbortError"));
        }

        const workerUrl = new URL("./jsonParserWorker.js", import.meta.url);
        const worker = new Worker(workerUrl, {
          type: "module",
        });

        const onAbort = () => {
          worker.terminate();
          reject(new DOMException("Aborted", "AbortError"));
        };
        signal?.addEventListener("abort", onAbort, { once: true });

        worker.postMessage(files["data"]);

        worker.onmessage = (event) => {
          signal?.removeEventListener("abort", onAbort);
          if (event.data.error) {
            worker.terminate();
            reject(event.data.error);
          } else {
            worker.terminate();
            resolve(event.data.result);
          }
        };

        // A module-worker load/instantiation failure (e.g. a stale hashed chunk 404ing after a
        // redeploy, or a client-side block) dispatches a plain `Event`, NOT the `ErrorEvent`
        // lib.dom declares for `onerror` — so the parameter is typed as what is really delivered
        // and the ErrorEvent members are read only behind the `instanceof` below. That mismatch is
        // what surfaced in Sentry as the untriageable string "Worker error: undefined" (no stack,
        // no URL). Reject with a real Error carrying the worker URL and any ErrorEvent details so
        // the failure is diagnosable.
        worker.onerror = (event: Event) => {
          signal?.removeEventListener("abort", onAbort);
          worker.terminate();
          const errorEvent = event instanceof ErrorEvent ? event : null;
          const thrown: unknown = errorEvent?.error;
          const detail =
            errorEvent?.message || (thrown instanceof Error ? thrown.message : "") || "worker script failed to load";
          const location = errorEvent?.filename
            ? ` (${errorEvent.filename}:${errorEvent.lineno}:${errorEvent.colno})`
            : "";
          const workerError = new Error(
            `jsonParser worker error: ${detail}${location} [worker: ${workerUrl.href}]`,
            thrown instanceof Error ? { cause: thrown } : undefined,
          );
          workerError.name = "JsonParserWorkerError";
          reject(workerError);
        };
      });
    });
  } else {
    const zip = new JSZip();
    const loadedZip = await zip.loadAsync(zippedData);
    const file = Object.values(loadedZip.files)[0];

    const fileContent = await file?.async("uint8array");
    const base64String = new TextDecoder().decode(fileContent);

    return base64String;
  }
}
